import { h } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { getToken } from "../api.js";

// ─────────────────────────────────────────────────────────────────────────────
// Privacy Policy & Terms of Service.
//
// These are plain-language, GDPR-aware baseline documents that describe what Kashtu
// ACTUALLY does (per the codebase), written for a single individual operator based in
// the EU/EEA. They are a sensible starting point, not formal legal advice — have a
// lawyer review before relying on them commercially.
//
// EDIT THESE THREE CONSTANTS:
//   CONTACT  — the published contact address (a privacy@kashtu.app alias is recommended).
//   COUNTRY  — your specific EU/EEA country; sets the governing-law clause. Until you set
//              it, the page renders a visible "[your EU/EEA country]" placeholder.
//   UPDATED  — bump whenever you change the wording (it's the "Last updated" line).
// ─────────────────────────────────────────────────────────────────────────────
const CONTACT = "privacy@kashtu.app";
const COUNTRY = "Germany";
const UPDATED = "6 June 2026";

// small builders
const p = (...c) => h("p", null, ...c);
const b = (t) => h("strong", null, t);
const li = (...c) => h("li", null, ...c);
const ul = (...items) => h("ul", null, ...items);
const sec = (title, ...nodes) => h("section", { class: "legal-sec" }, h("h2", null, title), ...nodes);
const mail = () => h("a", { href: `mailto:${CONTACT}` }, CONTACT);
const inlink = (href, label) => h("a", { href, "data-link": href }, label);

function page(title, intro, ...sections) {
  return h(
    "div",
    { class: "screen legal" },
    h(
      "div",
      { class: "legal-top" },
      h(
        "button",
        { class: "btn btn-ghost", style: { padding: "8px 14px" }, onClick: () => navigate(getToken() ? "/" : "/login") },
        icon("back", { size: 18 }),
        "Back"
      )
    ),
    h(
      "div",
      { class: "prose card" },
      h("h1", null, title),
      h("p", { class: "dim" }, `Last updated: ${UPDATED}`),
      intro,
      ...sections
    )
  );
}

// ── Privacy Policy ───────────────────────────────────────────────────────────
export function Privacy() {
  return page(
    "Privacy Policy",
    p(
      "Kashtu (“Kashtu”, “the app”, available at kashtu.app) is a personal, self-hosted workout tracker ",
      "run by an individual (“the operator”, “we”, “us”) based in the EU/EEA. The operator is the ",
      b("data controller"),
      " for the personal data described here. This policy explains what we collect, why, and the rights you have under the ",
      "General Data Protection Regulation (GDPR). Questions? Email ",
      mail(),
      "."
    ),
    sec(
      "1. Information you give us",
      ul(
        li(b("Account details:"), " a username, your password (kept only as a one-way bcrypt hash — we never store or see your plaintext password), and an email address (required to register; used for sign-in confirmation, password resets and account notices). You may add an optional display name."),
        li(b("Sign in with Google or GitHub (optional):"), " if you choose this, we receive a unique account identifier and your email address from that provider to create or link your account. We never receive your provider password."),
        li(b("Preferences:"), " units, theme, weekly goal, body-map preference, a public/private account setting, and notification preferences.")
      )
    ),
    sec(
      "2. Information you create by using Kashtu",
      ul(
        li(b("Training data:"), " workouts and templates, sessions, individual sets (weight, reps, RPE), bodyweight logs, programs, achievements and any notes you write."),
        li(b("Social content (only if you use these features):"), " workouts or programs you publish to Discover, ratings and reviews, follows, kudos, and reports you submit. Your username/display name and anything you publish are visible to other users — publicly, in the case of Discover content."),
        li(b("Progress photos:"), " these are stored ", b("only on your own device"), " (in your browser, via IndexedDB). They are never uploaded to our server and we have no access to them. Clearing your browser data or uninstalling the app removes them.")
      )
    ),
    sec(
      "3. Information collected automatically",
      ul(
        li(b("IP address:"), " processed transiently to keep the service secure (rate limiting and abuse prevention). The app sits behind Cloudflare, which handles your IP at the network edge; we use it only for security, not to profile you."),
        li(b("Sign-in token & preferences:"), " when you log in we store a sign-in token in your browser’s localStorage (not a cookie). Preferences are stored the same way."),
        li(b("Push subscription:"), " if you turn on notifications, your browser gives us a push-subscription endpoint that we store so we can send the reminders you asked for. You can turn this off at any time."),
        li(b("Timestamps:"), " account creation, last login and last activity."),
        li("We do ", b("not"), " use advertising cookies or third-party analytics/tracking.")
      )
    ),
    sec(
      "4. Cookies & local storage",
      p(
        "We don’t use tracking cookies. We use your browser’s localStorage to keep you signed in and remember preferences, and IndexedDB for on-device progress photos. ",
        "If the operator enables the Cloudflare Turnstile bot-check on the sign-up form, Cloudflare may set a cookie strictly to distinguish humans from bots."
      )
    ),
    sec(
      "5. Why we use your data, and our legal bases",
      ul(
        li(b("To provide the app"), " — managing your account and storing your training data: ", b("performance of a contract"), " (GDPR Art. 6(1)(b))."),
        li(b("To send essential account emails"), " (email verification, password reset): ", b("contract"), "."),
        li(b("To keep the service secure and prevent abuse"), " (rate limiting, bot checks, moderation): ", b("legitimate interests"), " (Art. 6(1)(f))."),
        li(b("To send push reminders / optional notifications:"), " your ", b("consent"), " (Art. 6(1)(a)), which you can withdraw any time."),
        li(b("To keep backups"), " for reliability: ", b("legitimate interests"), "."),
        li(b("Public and social features"), " operate only on data you choose to publish or share.")
      )
    ),
    sec(
      "6. Who we share data with (sub-processors)",
      p("We do ", b("not"), " sell your data or share it for advertising. To run the app we rely on a small number of service providers that process data on our behalf:"),
      ul(
        li(b("Cloudflare"), " — content delivery, TLS encryption, DDoS protection, the Turnstile bot check, and encrypted off-site backups (R2). Processes your IP and request metadata."),
        li(b("Resend"), " — delivers our transactional emails (verification, password reset). Processes your email address and the message content."),
        li(b("Google / GitHub"), " — only if you choose to sign in with them, via standard OAuth."),
        li(b("Gravatar (Automattic)"), " — if you have an email on file, your avatar is loaded from Gravatar using a hashed (MD5) form of your email. This means a hash of your email and your IP are disclosed to Gravatar when avatars are shown. Avatars only appear if Gravatar has an image registered for that email."),
        li(b("Other users"), " — content you publish or share is visible to them as described above.")
      ),
      p("We may also disclose data where we’re legally required to.")
    ),
    sec(
      "7. International transfers",
      p(
        "The operator and the primary database are in the EU/EEA. Some providers above (e.g. Cloudflare, Resend, Gravatar) are global and may process data outside the EEA under appropriate safeguards such as the European Commission’s Standard Contractual Clauses."
      )
    ),
    sec(
      "8. How long we keep it",
      p(
        "We keep your account data for as long as your account exists. When you delete your account (Profile → Data & account → Delete account), your account and the training and social data tied to it are ",
        b("permanently deleted immediately"),
        ". Residual copies may remain in encrypted rolling backups for up to about 30 days before ageing out. Server logs are short-lived."
      )
    ),
    sec(
      "9. Your rights",
      p("Under the GDPR you have the right to ", b("access"), ", ", b("rectify"), ", ", b("erase"), ", ", b("port"), ", ", b("restrict"), " and ", b("object"), " to the processing of your personal data, and to ", b("withdraw consent"), ". You can exercise the main ones yourself, in-app:"),
      ul(
        li(b("Export my data"), " — Profile → Data & account (a full JSON download)."),
        li(b("Delete account"), " — Profile → Data & account (permanent).")
      ),
      p("For anything else, email ", mail(), ". You also have the right to lodge a complaint with your local data-protection supervisory authority.")
    ),
    sec(
      "10. Security",
      p(
        "Passwords are hashed with bcrypt; all traffic is encrypted over HTTPS; we apply a Content-Security-Policy, per-IP rate limiting and a bot check. No system is perfectly secure, and because your sign-in token is held in your browser, please keep your device secure and sign out on shared devices."
      )
    ),
    sec(
      "11. Children",
      p("Kashtu is not intended for anyone under 16, and we don’t knowingly collect data from children under 16. If you believe a child has registered, contact ", mail(), " and we’ll remove the account.")
    ),
    sec(
      "12. Changes to this policy",
      p("We may update this policy from time to time. We’ll update the “Last updated” date above and, for material changes, may notify you in the app or by email.")
    ),
    sec(
      "13. Contact",
      p("The operator and data controller can be reached at ", mail(), ". See also our ", inlink("/terms", "Terms of Service"), ".")
    )
  );
}

// ── Terms of Service ─────────────────────────────────────────────────────────
export function Terms() {
  return page(
    "Terms of Service",
    p(
      "These Terms govern your use of Kashtu at kashtu.app, operated by an individual (“the operator”, “we”, “us”). ",
      "By creating an account or using the app you agree to these Terms and to our ",
      inlink("/privacy", "Privacy Policy"),
      ". If you don’t agree, please don’t use Kashtu."
    ),
    sec("1. Eligibility", p("You must be at least 16 years old to use Kashtu. By using it you confirm that you are.")),
    sec(
      "2. The service",
      p(
        "Kashtu is a free, personal project provided ",
        b("“as is” and “as available”"),
        ". It runs on modest self-hosted infrastructure as a hobby. We do not guarantee that it will be available, uninterrupted, error-free, or that your data will never be lost. Please ",
        b("export your data periodically"),
        " (Profile → Data & account). We may change, suspend or discontinue any part of the app at any time."
      )
    ),
    sec(
      "3. Not medical or fitness advice — train at your own risk",
      p(
        "Kashtu is a fitness-tracking tool. It is ",
        b("not a medical device"),
        " and is ",
        b("not a source of medical, health, or professional training advice"),
        ". Any suggestions it shows (for example automated weight/rep progression hints) are generic and computed automatically — they are not personalised advice. Exercise carries an inherent risk of injury. ",
        b("Consult a qualified physician before starting any exercise programme."),
        " You are solely responsible for how you train, for working within your own ability, and for your safety. You use Kashtu, and follow any workout, program or demo video found through it, ",
        b("entirely at your own risk"),
        "."
      )
    ),
    sec(
      "4. Your account",
      p(
        "Provide accurate information, keep your password secure, and don’t share your account. You are responsible for activity under your account. Tell us promptly at ",
        mail(),
        " if you suspect unauthorised use."
      )
    ),
    sec(
      "5. Acceptable use",
      p("You agree not to:"),
      ul(
        li("break the law, or use Kashtu for anything unlawful;"),
        li("harass, abuse, threaten, defame or impersonate anyone;"),
        li("publish or upload content you don’t have the right to share, or that is illegal, infringing, hateful, deceptive or harmful;"),
        li("spam, or post misleading or fraudulent content;"),
        li("attempt to disrupt, attack, overload, probe, reverse-engineer, scrape or gain unauthorised access to the service or to other users’ data;"),
        li("circumvent security measures, rate limits or the bot check; or"),
        li("bulk-extract content to build a competing dataset.")
      )
    ),
    sec(
      "6. Your content",
      p(
        "You keep ownership of the content you create (workouts, programs, notes, reviews, and so on). To operate the app, you grant the operator a non-exclusive, worldwide, royalty-free licence to store, reproduce and display your content as needed to provide the service. ",
        "Content you choose to publish (Discover workouts/programs, ratings, your profile) becomes visible to other users — and, for public content, to anyone. You are responsible for what you publish and confirm you have the rights to share it."
      )
    ),
    sec(
      "7. Third-party content & links",
      p(
        "Exercise demo links and external images point to third-party sites (for example YouTube or Vimeo) that we don’t control or endorse, and whose own terms apply. We are not responsible for third-party content."
      )
    ),
    sec(
      "8. Moderation",
      p(
        "We may review, remove or restrict content, and suspend, deactivate or delete accounts, where we reasonably believe they violate these Terms or harm users or the service. You can report content or users from within the app."
      )
    ),
    sec(
      "9. Termination",
      p(
        "You can stop using Kashtu and delete your account at any time (Profile → Data & account → Delete account). We may suspend or terminate your access for violations of these Terms or to protect the service. Provisions that by their nature should survive termination (disclaimers, limitation of liability, governing law) will survive."
      )
    ),
    sec(
      "10. Disclaimers",
      p(
        "To the maximum extent permitted by law, Kashtu is provided ",
        b("“as is” and “as available”"),
        " without warranties of any kind. We don’t warrant that it will be secure, available, accurate or error-free, or that data will not be lost."
      )
    ),
    sec(
      "11. Limitation of liability",
      p(
        "To the maximum extent permitted by applicable law, the operator will not be liable for any indirect, incidental, special or consequential damages, for lost data or profits, or for any injury resulting from exercise, arising from your use of (or inability to use) Kashtu. ",
        "Because Kashtu is provided free of charge, the operator’s total liability is limited accordingly. ",
        b("Nothing in these Terms limits liability that cannot be limited by law"),
        " — including liability for death or personal injury caused by negligence, for fraud, or your mandatory statutory consumer rights."
      )
    ),
    sec(
      "12. Changes to these Terms",
      p("We may update these Terms. We’ll change the “Last updated” date above; continuing to use Kashtu after a change means you accept the updated Terms.")
    ),
    sec(
      "13. Governing law",
      p(
        "These Terms are governed by the laws of ",
        b(COUNTRY),
        ", without regard to conflict-of-law rules. If you are a consumer, you also keep the benefit of any mandatory consumer-protection rules of your country of residence, which these Terms do not override."
      )
    ),
    sec("14. Contact", p("Questions about these Terms? Email ", mail(), "."))
  );
}

/** Privacy · Terms footer row, reused by the auth screen and the profile screen. */
export function legalLinks() {
  return h(
    "div",
    { class: "legal-links" },
    h("a", { href: "/privacy", "data-link": "/privacy" }, "Privacy"),
    h("span", null, " · "),
    h("a", { href: "/terms", "data-link": "/terms" }, "Terms")
  );
}
